import type { KnowledgeFigureAnnotation } from "./types.js";

type Region = KnowledgeFigureAnnotation["region"];
type Point = KnowledgeFigureAnnotation["marker"];
type LabelBox = NonNullable<KnowledgeFigureAnnotation["labelBox"]>;

function mark(
  label: string,
  description: string,
  region: Region,
  marker: Point,
): KnowledgeFigureAnnotation {
  return { label, description, region, marker };
}

function callout(
  label: string,
  description: string,
  region: Region,
  labelBox: LabelBox,
  target: Point,
  height = 48,
): KnowledgeFigureAnnotation {
  return {
    label,
    description,
    region,
    labelBox,
    target,
    marker: [labelBox[0] + height / 2, labelBox[1] + height / 2],
  };
}

/** Labels describe the captured controls, not a new product workflow. */
export const KNOWLEDGE_FIGURE_ANNOTATIONS: Readonly<
  Record<string, readonly KnowledgeFigureAnnotation[]>
> = {
  "platform-launcher": [
    callout(
      "Enabled apps",
      "Choose an app to open its library.",
      [310, 210, 1108, 206],
      [730, 450, 260],
      [865, 416],
    ),
    callout(
      "Learning guides",
      "Open Knowledge Studio for learning paths and help with the software.",
      [310, 434, 265, 186],
      [730, 550, 290],
      [575, 540],
    ),
    callout(
      "Personal settings",
      "Open Settings to set your own preferences.",
      [310, 683, 265, 169],
      [730, 750, 310],
      [575, 766],
    ),
  ],
  "platform-preferences": [
    callout(
      "Theme",
      "Choose Match device, Light or Dark.",
      [764, 60, 310, 37],
      [495, 61, 195],
      [764, 78],
      36,
    ),
    callout(
      "High contrast",
      "Use the separate contrast switch when it makes the screen easier to read.",
      [1037, 137, 39, 25],
      [720, 130, 260],
      [1037, 149],
      36,
    ),
    callout(
      "Time zone",
      "Choose the time zone used to display dates and times.",
      [829, 278, 246, 40],
      [515, 278, 245],
      [829, 298],
      36,
    ),
  ],
  "player-ai-preferences": [
    callout(
      "Tone",
      "Choose Direct or Empathetic for your conversations.",
      [910, 181, 180, 37],
      [605, 181, 220],
      [910, 200],
      36,
    ),
    callout(
      "Mood history",
      "Decide whether Player AI can use your mood history.",
      [1050, 324, 39, 24],
      [605, 316, 260],
      [1050, 336],
      36,
    ),
    callout(
      "Auto approval",
      "Check this preference before allowing AI actions to proceed automatically.",
      [1050, 434, 39, 24],
      [605, 422, 265],
      [1050, 446],
      36,
    ),
    callout(
      "Default profile",
      "Choose the profile used when you start a chat.",
      [800, 548, 289, 41],
      [465, 550, 280],
      [800, 568],
      36,
    ),
  ],
  "solve-create": [
    mark(
      "Neutral title",
      "Name the observed problem without assuming its cause or solution.",
      [15, 101, 529, 39],
      [543, 88],
    ),
    mark(
      "Measured gap",
      "Describe the current condition and the gap you need to investigate.",
      [15, 194, 529, 103],
      [543, 181],
    ),
    mark(
      "Create SOLVE",
      "Create the workspace once the starting description is clear.",
      [424, 459, 121, 41],
      [531, 444],
    ),
  ],
  "solve-widget-picker": [
    mark("Search", "Find a widget by name.", [7, 6, 305, 42], [292, 27]),
    mark(
      "Phase tools",
      "Start with the tools suggested for the current phase.",
      [7, 81, 305, 241],
      [292, 106],
    ),
    mark(
      "Flexible widgets",
      "Add supporting content when it helps answer your question.",
      [7, 351, 305, 87],
      [292, 373],
    ),
  ],
  "solve-specify": [
    callout(
      "Phase navigation",
      "See the current phase and its navigation controls.",
      [15, 61, 255, 48],
      [325, 64, 325],
      [270, 85],
    ),
    callout(
      "Phase workspace",
      "Keep the evidence for Specify in its workspace area.",
      [654, 234, 827, 698],
      [250, 260, 330],
      [654, 284],
    ),
    callout(
      "Problem Statement",
      "Record the observed gap before investigating causes.",
      [687, 329, 353, 328],
      [255, 465, 345],
      [687, 491],
    ),
    callout(
      "Add widget",
      "Open the picker to add another tool or supporting content.",
      [1458, 63, 106, 43],
      [1210, 142, 260],
      [1510, 106],
    ),
  ],
  "solve-people": [
    callout(
      "People panel",
      "Review access to this SOLVE in People.",
      [1328, 50, 397, 882],
      [1050, 128, 245],
      [1328, 152],
    ),
    callout(
      "Find people",
      "Search for a person when you need to add workspace access.",
      [1343, 193, 370, 42],
      [1050, 238, 245],
      [1343, 214],
    ),
    callout(
      "Workspace role",
      "The Owner label describes this person's role in this SOLVE.",
      [1653, 338, 60, 27],
      [1370, 427, 315],
      [1681, 365],
    ),
  ],
  "vsm-current-map": [
    callout(
      "Symbol palette",
      "Choose process, inventory and flow symbols from the palette.",
      [1, 45, 173, 792],
      [235, 135, 300],
      [174, 180],
    ),
    callout(
      "Current-state map",
      "Lay out the observed process and its material and information flows.",
      [555, 242, 752, 226],
      [775, 153, 350],
      [930, 242],
    ),
    callout(
      "Lead-time ladder",
      "Read processing and waiting time together below the map.",
      [555, 488, 752, 127],
      [775, 655, 335],
      [930, 615],
    ),
  ],
  "vsm-palette": [
    mark(
      "Search symbols",
      "Search shapes and flows instead of scrolling through every group.",
      [7, 50, 305, 40],
      [289, 70],
    ),
    mark(
      "Essentials",
      "Find the core process, supplier and inventory symbols here.",
      [7, 104, 305, 241],
      [290, 119],
    ),
    mark(
      "Material flow",
      "Choose a flow arrow that matches what actually happens.",
      [7, 760, 305, 74],
      [289, 774],
    ),
  ],
  "vsm-process-data": [
    mark(
      "Facts and targets",
      "Keep observed Facts separate from proposed Targets; use Connections for links.",
      [19, 286, 279, 49],
      [323, 310],
    ),
    mark(
      "Observed values",
      "Enter the value and its unit for each process measure.",
      [617, 434, 323, 201],
      [596, 454],
    ),
    mark(
      "Save controls",
      "Save changes, or save and move to the next step.",
      [679, 719, 268, 41],
      [934, 697],
    ),
  ],
  "vsm-sipoc": [
    mark(
      "Suppliers",
      "Who supplies what this step needs?",
      [97, 152, 295, 40],
      [368, 172],
    ),
    mark(
      "Inputs",
      "What enters the step, including required information?",
      [393, 152, 294, 40],
      [663, 172],
    ),
    mark(
      "Process",
      "Place the high-level steps in sequence.",
      [688, 152, 352, 40],
      [1016, 172],
    ),
    mark(
      "Outputs",
      "What leaves the step, and what requirements must it meet?",
      [1041, 152, 295, 40],
      [1312, 172],
    ),
    mark(
      "Customers",
      "Who receives the output from this step?",
      [1337, 152, 294, 40],
      [1607, 172],
    ),
  ],
  "vsm-kaizen-health": [
    mark(
      "Overall health",
      "Read the overall signal before checking individual categories.",
      [19, 79, 364, 40],
      [296, 98],
    ),
    mark(
      "Execution risk",
      "Check At risk, Blocked and Overdue for work that needs attention.",
      [19, 127, 364, 134],
      [193, 207],
    ),
    mark(
      "Unassigned work",
      "Find actions that still need a responsible person.",
      [19, 266, 181, 66],
      [176, 315],
    ),
  ],
  "hoshin-create": [
    mark(
      "Plan name",
      "Name the direction or improvement the plan supports.",
      [15, 100, 529, 39],
      [542, 87],
    ),
    mark(
      "Purpose",
      "Explain what the plan is about in the description.",
      [15, 194, 529, 85],
      [542, 181],
    ),
    mark(
      "Create Hoshin",
      "Create the plan, then build its objectives and targets.",
      [422, 334, 123, 40],
      [531, 319],
    ),
  ],
  "hoshin-ideate": [
    callout(
      "Your idea area",
      "Start in your own area before sharing ideas with the workshop.",
      [522, 225, 562, 365],
      [1160, 246, 305],
      [1084, 270],
    ),
    callout(
      "Add an idea",
      "Use the tile to record a new idea.",
      [580, 291, 212, 155],
      [240, 351, 275],
      [580, 375],
    ),
    callout(
      "Submit idea",
      "Submit your idea when it is ready for the workshop.",
      [1547, 17, 132, 40],
      [1310, 121, 285],
      [1608, 57],
    ),
    callout(
      "Present ideas",
      "Use the next workshop stage to share the submitted ideas.",
      [273, 16, 136, 42],
      [273, 120, 305],
      [340, 58],
    ),
  ],
  "hoshin-x-matrix": [
    callout(
      "Long-term objective",
      "Set the longer-term result you are working towards.",
      [120, 709, 1235, 51],
      [557, 652, 355],
      [736, 709],
    ),
    callout(
      "Annual objective",
      "Define the measurable improvement needed this year.",
      [20, 182, 53, 528],
      [180, 362, 320],
      [73, 446],
    ),
    callout(
      "Improvement priority",
      "Link the annual objective to the work that should deliver it.",
      [120, 82, 1235, 51],
      [555, 204, 385],
      [737, 133],
    ),
    callout(
      "Target to improve",
      "Connect the priority to a measure that shows progress.",
      [1355, 182, 51, 528],
      [1005, 371, 335],
      [1355, 446],
    ),
    callout(
      "Primary owner",
      "The filled dot marks the primary owner of the priority.",
      [1455, 83, 54, 626],
      [1090, 7, 315],
      [1483, 107],
    ),
  ],
  "hoshin-monitor": [
    callout(
      "Status filters",
      "Narrow the chart to commitments with a particular status.",
      [39, 89, 322, 29],
      [410, 75, 300],
      [361, 104],
    ),
    callout(
      "Target and actual",
      "Compare planned targets with actual results across the review period.",
      [609, 129, 996, 68],
      [1030, 214, 355],
      [1090, 197],
    ),
    callout(
      "Date range",
      "Choose the period displayed in the Bowling chart.",
      [1474, 88, 215, 30],
      [1130, 75, 280],
      [1474, 102],
    ),
  ],
  "lama-assessment": [
    mark(
      "Your answer",
      "Answer the question from the stated assessment scope.",
      [27, 219, 817, 85],
      [821, 205],
    ),
    mark(
      "Optional context",
      "Use Comment to explain evidence or limits behind your answer.",
      [27, 351, 817, 85],
      [821, 338],
    ),
    mark(
      "Review and submit",
      "Review the whole response before submitting it.",
      [686, 540, 175, 39],
      [664, 560],
    ),
  ],
  "admin-add-people": [
    mark(
      "Directory member",
      "Find the person in the connected company directory.",
      [88, 491, 432, 41],
      [531, 478],
    ),
    mark(
      "Workspace role",
      "Choose the person's workspace role before adding them.",
      [88, 561, 432, 41],
      [531, 548],
    ),
    mark(
      "Transformation Lead",
      "This additional grant is separate from the workspace role.",
      [87, 656, 292, 34],
      [405, 671],
    ),
  ],
  "admin-identity": [
    mark(
      "Entra sign-in",
      "Check the sign-in status separately from directory provisioning.",
      [294, 316, 272, 128],
      [541, 429],
    ),
    mark(
      "Directory sync",
      "Review the directory's people, groups and sync status.",
      [576, 316, 271, 128],
      [822, 429],
    ),
    mark(
      "Shared Teams",
      "Check the shared Teams and their accepted memberships.",
      [857, 316, 272, 128],
      [1104, 429],
    ),
    mark(
      "Access sync",
      "Check queued or failed access changes.",
      [1138, 316, 272, 128],
      [1384, 429],
    ),
  ],
  "admin-provisioning": [
    mark(
      "Provisioning tab",
      "Manage the OAuth clients used by your identity provider here.",
      [161, 185, 111, 41],
      [264, 172],
    ),
    mark(
      "Generate credentials",
      "Generate a client when you are ready to configure the provider.",
      [70, 469, 168, 40],
      [252, 489],
    ),
    mark(
      "Client configuration",
      "The secret is shown once; copy the complete configuration before closing the confirmation.",
      [70, 519, 466, 96],
      [520, 599],
    ),
  ],
  "admin-organization": [
    mark(
      "Organization name",
      "Set the name displayed across the workspace.",
      [6, 139, 432, 39],
      [424, 126],
    ),
    mark(
      "Short name",
      "Use up to six characters; the first two form the organization mark.",
      [6, 233, 432, 39],
      [424, 219],
    ),
    mark(
      "Company domain",
      "This is descriptive; it does not control sign-in or prove ownership.",
      [6, 327, 432, 40],
      [424, 313],
    ),
  ],
  "admin-modules": [
    callout(
      "App availability",
      "Use each app's switch to control its workspace availability.",
      [294, 181, 1116, 365],
      [740, 562, 325],
      [896, 546],
    ),
    callout(
      "Email capability",
      "Email is configured separately from the apps listed above.",
      [294, 668, 1116, 107],
      [740, 810, 325],
      [896, 775],
    ),
  ],
  "admin-mail-server": [
    mark(
      "SMTP endpoint",
      "Set the server address, port and security mode together.",
      [15, 194, 473, 188],
      [474, 182],
    ),
    mark(
      "Secret reference",
      "Enter the supported secret-store reference for the password, not the password itself.",
      [15, 510, 473, 39],
      [474, 497],
    ),
    mark(
      "Sender identity",
      "Set the From address and the name recipients should see.",
      [15, 637, 473, 112],
      [474, 625],
    ),
  ],
  "admin-templates": [
    mark(
      "Delivery setup",
      "Check the mail-server warning before expecting messages to be sent.",
      [70, 239, 466, 125],
      [520, 255],
    ),
    mark(
      "Find a template",
      "Search messages and filter by wording state or readiness.",
      [70, 580, 466, 179],
      [520, 566],
    ),
    mark(
      "Template actions",
      "Open the message's action menu to work with its wording.",
      [487, 822, 37, 37],
      [475, 840],
    ),
  ],
  "admin-ai-connection": [
    mark(
      "Provider",
      "Choose the provider this connection will use.",
      [15, 183, 474, 39],
      [474, 169],
    ),
    mark(
      "Secret reference",
      "Enter the supported credential reference; never paste the secret value here.",
      [15, 331, 474, 40],
      [474, 318],
    ),
    mark(
      "Save connection",
      "Save the connection, then create profiles for its models and settings.",
      [349, 507, 141, 40],
      [474, 493],
    ),
  ],
  "admin-ai-defaults": [
    mark(
      "Platform default",
      "Choose the profile apps can inherit as their default.",
      [15, 308, 1113, 41],
      [1090, 295],
    ),
    mark(
      "App defaults",
      "Choose an explicit profile for an app or let it inherit the platform default.",
      [15, 380, 1113, 262],
      [1090, 367],
    ),
    mark(
      "Save defaults",
      "Save the selected defaults after reviewing their availability.",
      [14, 752, 120, 37],
      [155, 770],
    ),
  ],
  "admin-integration": [
    mark(
      "Connection name",
      "Use a name that identifies the account or team this integration serves.",
      [91, 358, 425, 40],
      [523, 345],
    ),
    mark(
      "Site URL",
      "Enter the one Atlassian site this connector may reach.",
      [91, 709, 425, 40],
      [523, 695],
    ),
    mark(
      "Create integration",
      "Save the configuration, then connect your account and review access.",
      [91, 801, 149, 40],
      [324, 821],
    ),
  ],
  "admin-adoption-tours": [
    callout(
      "Reset every tour",
      "Offer all tours again to everyone in the workspace.",
      [871, 19, 206, 41],
      [551, 19, 280],
      [871, 39],
      36,
    ),
    callout(
      "Reset one tour",
      "Use the row's action to replay only that tour for everyone.",
      [913, 144, 153, 225],
      [559, 209, 290],
      [913, 226],
      36,
    ),
  ],
  "admin-audit": [
    mark(
      "Event filters",
      "Narrow records by source service, action or actor.",
      [31, 191, 805, 59],
      [878, 230],
    ),
    mark(
      "Date range",
      "Set From and To, then apply the range.",
      [31, 285, 799, 62],
      [878, 324],
    ),
    mark(
      "Routine activity",
      "Include routine activity when it helps your investigation.",
      [31, 376, 41, 25],
      [280, 388],
    ),
  ],
};
